/**
 * Chart-only color palettes. Used for bar and line charts independent of app theme.
 * Each palette uses distinct hues so multi-series charts get different colors.
 * "theme" now resolves to the Repo-2 CHG default for this v2 bot; "custom" = user-saved colors; others = fixed palettes.
 */
export const CHART_PALETTE_IDS = [
  'theme',
  'brand',
  'financial',
  'soft',
  'contrast',
  'modern',
  'capital_markets',
  'portfolio_growth',
  'bull_market',
  'trading_floor',
  'gold_reserves',
  'hedge_fund',
  'quant_models',
  'risk_matrix',
  'market_heatmap',
  'asset_allocation',
  'custom',
] as const;
export type ChartPaletteId = (typeof CHART_PALETTE_IDS)[number];

/** All chart palettes: general + finance-themed. */
export const CHART_PALETTES: Record<Exclude<ChartPaletteId, 'theme' | 'custom'>, string[]> = {
  brand: [
    '#2C5F7C', '#E8B84F', '#5E8B5E', '#D97757', '#6FB0D1',
    '#A63D40', '#8CB58C', '#8A8178', '#6E3450', '#4E7C89',
  ],
  financial: [
    '#2563EB', '#06B6D4', '#14B8A6', '#22C55E', '#84CC16',
    '#F59E0B', '#F97316', '#EF4444', '#8B5CF6', '#64748B',
  ],
  soft: [
    '#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F',
    '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC',
  ],
  contrast: [
    '#1D4ED8', '#0891B2', '#0F766E', '#15803D', '#65A30D',
    '#D97706', '#C2410C', '#B91C1C', '#6D28D9', '#BE185D',
  ],
  modern: [
    '#6366F1', '#3B82F6', '#0EA5E9', '#22D3EE', '#10B981',
    '#22C55E', '#F59E0B', '#FB923C', '#F43F5E', '#A855F7',
  ],
  capital_markets: [
    '#1E3A8A', '#1D4ED8', '#2563EB', '#3B82F6', '#60A5FA',
    '#93C5FD', '#0EA5E9', '#0284C7', '#0369A1', '#075985',
    '#4F46E5', '#4338CA', '#3730A3', '#312E81', '#6366F1',
  ],
  portfolio_growth: [
    '#14532D', '#166534', '#15803D', '#16A34A', '#22C55E',
    '#4ADE80', '#86EFAC', '#BBF7D0', '#DCFCE7', '#ECFDF5',
    '#84CC16', '#65A30D', '#4D7C0F', '#365314', '#A3E635',
  ],
  bull_market: [
    '#065F46', '#047857', '#059669', '#10B981', '#34D399',
    '#6EE7B7', '#A7F3D0', '#D1FAE5', '#ECFDF5', '#064E3B',
    '#0F766E', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4',
  ],
  trading_floor: [
    '#7C2D12', '#9A3412', '#C2410C', '#EA580C', '#F97316',
    '#FB923C', '#FDBA74', '#FED7AA', '#FFF7ED', '#7F1D1D',
    '#991B1B', '#B91C1C', '#DC2626', '#EF4444', '#F87171',
  ],
  gold_reserves: [
    '#78350F', '#92400E', '#B45309', '#D97706', '#F59E0B',
    '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7', '#451A03',
    '#78350F', '#A16207', '#CA8A04', '#EAB308', '#FEF9C3',
  ],
  hedge_fund: [
    '#4C1D95', '#5B21B6', '#6D28D9', '#7C3AED', '#8B5CF6',
    '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE', '#581C87',
    '#6B21A8', '#7E22CE', '#9333EA', '#A855F7', '#C084FC',
  ],
  quant_models: [
    '#0F172A', '#1E293B', '#334155', '#475569', '#64748B',
    '#94A3B8', '#CBD5F5', '#E2E8F0', '#F1F5F9', '#020617',
    '#111827', '#1F2937', '#374151', '#4B5563', '#6B7280',
  ],
  risk_matrix: [
    '#7F1D1D', '#991B1B', '#B91C1C', '#DC2626', '#EF4444',
    '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2', '#450A0A',
    '#7F1D1D', '#9F1239', '#BE123C', '#E11D48', '#FB7185',
  ],
  market_heatmap: [
    '#164E63', '#155E75', '#0E7490', '#0891B2', '#06B6D4',
    '#22D3EE', '#67E8F9', '#A5F3FC', '#CFFAFE', '#083344',
    '#164E63', '#0C4A6E', '#075985', '#0369A1', '#0284C7',
  ],
  asset_allocation: [
    '#3F6212', '#4D7C0F', '#65A30D', '#84CC16', '#A3E635',
    '#BEF264', '#D9F99D', '#ECFCCB', '#F7FEE7', '#365314',
    '#4D7C0F', '#6B8E23', '#7FBF3F', '#9ACD32', '#ADFF2F',
  ],
};

export const CHART_PALETTE_OPTIONS: { id: ChartPaletteId; label: string; colors?: string[] }[] = [
  { id: 'theme', label: 'Repo 2 Default' },
  { id: 'brand', label: 'CHG / Repo 2', colors: CHART_PALETTES.brand },
  { id: 'financial', label: 'Financial', colors: CHART_PALETTES.financial },
  { id: 'soft', label: 'Soft Analytics', colors: CHART_PALETTES.soft },
  { id: 'contrast', label: 'High Contrast', colors: CHART_PALETTES.contrast },
  { id: 'modern', label: 'Modern Tech', colors: CHART_PALETTES.modern },
  { id: 'capital_markets', label: 'Capital Markets (Blues & Indigo)', colors: CHART_PALETTES.capital_markets },
  { id: 'portfolio_growth', label: 'Portfolio Growth (Greens)', colors: CHART_PALETTES.portfolio_growth },
  { id: 'bull_market', label: 'Bull Market (Teal & Emerald)', colors: CHART_PALETTES.bull_market },
  { id: 'trading_floor', label: 'Trading Floor (Orange & Red)', colors: CHART_PALETTES.trading_floor },
  { id: 'gold_reserves', label: 'Gold Reserves (Amber)', colors: CHART_PALETTES.gold_reserves },
  { id: 'hedge_fund', label: 'Hedge Fund (Violet & Purple)', colors: CHART_PALETTES.hedge_fund },
  { id: 'quant_models', label: 'Quant Models (Slate & Gray)', colors: CHART_PALETTES.quant_models },
  { id: 'risk_matrix', label: 'Risk Matrix (Reds)', colors: CHART_PALETTES.risk_matrix },
  { id: 'market_heatmap', label: 'Market Heatmap (Cyan)', colors: CHART_PALETTES.market_heatmap },
  { id: 'asset_allocation', label: 'Asset Allocation (Lime Green)', colors: CHART_PALETTES.asset_allocation },
  { id: 'custom', label: 'Custom' },
];

/** Default custom palette */
export const DEFAULT_CUSTOM_CHART_COLORS = [
  '#2C5F7C', '#E8B84F', '#5E8B5E', '#D97757',
  '#6FB0D1', '#A63D40', '#8CB58C', '#8A8178',
];

export function getChartPaletteColors(paletteId: ChartPaletteId): string[] | null {
  if (paletteId === 'theme' || paletteId === 'custom') return null;
  return CHART_PALETTES[paletteId] ?? null;
}
